import React from 'react';
import { Plus, X } from 'lucide-react';
import { WEEKDAYS, EXCHANGE_TIME_PRESETS } from './constants';

// Overnight / exchange assignment cycles: Empty → Plaintiff → Defendant → Exchange → Empty
var OVERNIGHT_CYCLE = ['', 'plaintiff', 'defendant', 'exchange'];

// Maps a preset parenting-time schedule pattern to a 4x7 grid of overnight assignments.
// Days are ordered Sun, Mon, Tue, Wed, Thu, Fri, Sat (to match WEEKDAYS).
// P = 'plaintiff' (assumed custodial/primary), D = 'defendant' (assumed secondary), X = 'exchange'.
// Returns null for 'custom' so the existing user grid is preserved.
var buildGridForPattern = function(patternId) {
  var P = 'plaintiff';
  var D = 'defendant';
  if (patternId === '2-2-3') {
    // 2-2-3 rotation (Sun,Mon,Tue,Wed,Thu,Fri,Sat)
    var w1 = [P, P, P, D, D, P, P];
    var w2 = [D, D, D, P, P, D, D];
    return [w1, w2, w1.slice(), w2.slice()];
  }
  if (patternId === '3-4') {
    // 3-4-4-3 rotation
    // Wk1: Sun=D, Mon-Wed=P, Thu-Sat=D   (3 P, 4 D)
    // Wk2: Sun=D, Mon-Thu=P, Fri-Sat=D   (4 P, 3 D)
    var w1b = [D, P, P, P, D, D, D];
    var w2b = [D, P, P, P, P, D, D];
    return [w1b, w2b, w1b.slice(), w2b.slice()];
  }
  if (patternId === 'week-on') {
    // Week On / Week Off — alternating full weeks
    var wP = [P, P, P, P, P, P, P];
    var wD = [D, D, D, D, D, D, D];
    return [wP, wD, wP.slice(), wD.slice()];
  }
  if (patternId === '5-2') {
    // 5-2 split: Plaintiff Mon-Fri, Defendant Sat+Sun — repeats weekly
    var row = [D, P, P, P, P, P, D];
    return [row, row.slice(), row.slice(), row.slice()];
  }
  return null; // custom or unknown — preserve existing grid
};

// EOW needs a slightly more nuanced layout than a single row definition above allows.
// Dedicated builder so the rotation is clear for court output.
var buildEOWGrid = function() {
  var P = 'plaintiff';
  var D = 'defendant';
  var X = 'exchange';
  // Wk1: Mon-Thu = P, Fri-Sat = D overnights, Sun before = P, Sun after (Wk2 Sun) = exchange back to P.
  // Using Sun-first ordering:
  var w1 = [P, P, P, P, P, D, D]; // Sun P, Mon P, Tue P, Wed P, Thu P, Fri D, Sat D
  var w2 = [X, P, P, P, P, P, P]; // Sun exchange back, rest P (non-visit week)
  var w3 = [P, P, P, P, P, D, D]; // Wk3: Defendant weekend again
  var w4 = [X, P, P, P, P, P, P]; // Wk4: exchange-back Sun, rest P
  return [w1, w2, w3, w4];
};

export const StageSchedule = function({ planData, updateData, setPlanData }) {
        // Apply a preset pattern: sets schedulePattern and auto-populates weekGridOvernight.
        var applyPattern = function(patternId) {
          var grid = patternId === 'eow' ? buildEOWGrid() : buildGridForPattern(patternId);
          // If there's existing overnight data and we're about to overwrite with a preset, confirm.
          if (grid) {
            var hasExisting = (planData.weekGridOvernight || []).some(function(w) {
              return (w || []).some(function(c) { return c && c.length > 0; });
            });
            if (hasExisting && !window.confirm('This will replace your current 4-Week Overnight Schedule with the selected pattern. Continue?')) {
              return;
            }
          }
          setPlanData(function(prev) {
            var next = Object.assign({}, prev, { schedulePattern: patternId });
            if (grid) {
              next.weekGridOvernight = grid;
            }
            return next;
          });
        };
        var rotateOvernight = function(weekIdx, dayIdx, e) {
          if (e) e.stopPropagation();
          var grid = (planData.weekGridOvernight || [['','','','','','',''],['','','','','','',''],['','','','','','',''],['','','','','','','']]).map(function(w) { return w.slice(); });
          var cur = grid[weekIdx][dayIdx];
          var nextIdx = (OVERNIGHT_CYCLE.indexOf(cur) + 1) % OVERNIGHT_CYCLE.length;
          grid[weekIdx][dayIdx] = OVERNIGHT_CYCLE[nextIdx];
          updateData('weekGridOvernight', grid);
        };

        // Scheduled events handlers (supervised visitation sessions, therapy, etc.)
        var addScheduleEvent = function() {
          setPlanData(function(prev) {
            return Object.assign({}, prev, {
              scheduleEvents: (prev.scheduleEvents || []).concat([{
                id: 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
                type: 'supervised-visitation',
                name: '',
                dayOfWeek: '',
                frequency: 'weekly',
                time: '',
                duration: '',
                location: '',
                notes: '',
              }])
            });
          });
        };
        var updateScheduleEvent = function(idx, field, val) {
          setPlanData(function(prev) {
            var updated = (prev.scheduleEvents || []).map(function(ev, i) {
              return i === idx ? Object.assign({}, ev, { [field]: val }) : ev;
            });
            return Object.assign({}, prev, { scheduleEvents: updated });
          });
        };
        var removeScheduleEvent = function(idx) {
          setPlanData(function(prev) {
            return Object.assign({}, prev, {
              scheduleEvents: (prev.scheduleEvents || []).filter(function(_, i) { return i !== idx; })
            });
          });
        };
        var scheduleEvents = planData.scheduleEvents || [];
  return (

          <div className="space-y-6">
            {/* Schedule Patterns */}
            <div>
              <label className="block text-sm font-medium mb-2">Parenting Time Schedule Pattern</label>
              <p className="text-xs text-[#3A3566] mb-3">Selecting a preset will auto-populate the 4-Week Overnight Schedule below. Choose "Custom Schedule" to build the grid manually.</p>
              <div className="space-y-3">
                {[
                  { id: 'eow', name: 'Every Other Weekend (EOW)', desc: 'Alternating weekends with one parent' },
                  { id: '2-2-3', name: '2-2-3 Pattern', desc: '2 days, 2 days, 3 days rotating' },
                  { id: '3-4', name: '3-4-4-3 Pattern', desc: '3 days, 4 days, 4 days, 3 days' },
                  { id: 'week-on', name: 'Week On/Week Off', desc: '7-day alternating schedule' },
                  { id: '5-2', name: '5-2 Split', desc: '5 days with one, 2 days with other' },
                  { id: 'custom', name: 'Custom Schedule', desc: 'Use the 4-week grid below to define your own schedule' },
                ].map(function(pattern) {
                  return (
                    <button key={pattern.id} onClick={function() { applyPattern(pattern.id); }}
                      data-testid={'schedule-' + pattern.id}
                      className={planData.schedulePattern === pattern.id
                        ? 'w-full p-4 rounded-lg border text-left transition-all border-[#10B981] bg-[#10B981]/10'
                        : 'w-full p-4 rounded-lg border text-left transition-all border-white/10 hover:border-white/20'}>
                      <p className="font-semibold mb-1">{pattern.name}</p>
                      <p className="text-sm text-white/60">{pattern.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Out-of-State Parent */}
            <div className="p-4 bg-[#EAB308]/5 border border-[#EAB308]/20 rounded-xl space-y-3" data-testid="out-of-state-section">
              <label className="block text-sm font-medium">Is one parent out of state?</label>
              <div className="flex gap-3">
                <button onClick={function() { updateData('parentOutOfState', 'yes'); }}
                  className={'px-4 py-2 rounded-lg border text-sm font-medium transition-all ' + (planData.parentOutOfState === 'yes' ? 'border-[#EAB308] bg-[#EAB308]/10 text-[#EAB308]' : 'border-white/10 text-white/50 hover:border-white/20')}
                  data-testid="out-of-state-yes">Yes</button>
                <button onClick={function() { updateData('parentOutOfState', 'no'); }}
                  className={'px-4 py-2 rounded-lg border text-sm font-medium transition-all ' + (planData.parentOutOfState === 'no' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10 text-[#5A4E9E]' : 'border-white/10 text-white/50 hover:border-white/20')}
                  data-testid="out-of-state-no">No</button>
              </div>

              {planData.parentOutOfState === 'yes' && (
                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Which parent is out of state?</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={function() { updateData('outOfStateParent', 'plaintiff'); }}
                        className={'p-2.5 rounded-lg border text-sm font-medium text-center transition-all ' + (planData.outOfStateParent === 'plaintiff' ? 'border-[#EAB308] bg-[#EAB308]/10' : 'border-white/10 hover:border-white/20')}
                        data-testid="oos-plaintiff">Plaintiff</button>
                      <button onClick={function() { updateData('outOfStateParent', 'defendant'); }}
                        className={'p-2.5 rounded-lg border text-sm font-medium text-center transition-all ' + (planData.outOfStateParent === 'defendant' ? 'border-[#EAB308] bg-[#EAB308]/10' : 'border-white/10 hover:border-white/20')}
                        data-testid="oos-defendant">Defendant</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">School Year Schedule</label>
                    <textarea value={planData.schoolYearSchedule || ''} onChange={function(e) { updateData('schoolYearSchedule', e.target.value); }}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2.5 h-20 resize-y text-sm focus:outline-none focus:border-[#EAB308]/40"
                      placeholder="e.g. Child resides with in-state parent during school year. Out-of-state parent gets one weekend per month and school breaks..."
                      data-testid="school-year-schedule" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Summer Schedule</label>
                    <textarea value={planData.summerSchedule || ''} onChange={function(e) { updateData('summerSchedule', e.target.value); }}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2.5 h-20 resize-y text-sm focus:outline-none focus:border-[#EAB308]/40"
                      placeholder="e.g. Child spends 6 weeks with out-of-state parent during summer. Travel arrangements split equally..."
                      data-testid="summer-schedule" />
                  </div>
                </div>
              )}
            </div>

            {/* 4-Week Overnight Schedule */}
            <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="schedule-calendar">
              <div className="bg-white/5 p-4">
                <p className="text-sm font-semibold mb-1 text-[#1E1A3E]">4-Week Overnight Schedule Editor</p>
                <div className="text-xs text-[#3A3566] mb-2 space-y-1.5 leading-relaxed">
                  <p><span className="font-semibold text-[#2D2560]">How to edit:</span> Click <span className="font-semibold">anywhere</span> inside a day cell to cycle its assignment: Empty &rarr; Plaintiff (Blue) &rarr; Defendant (Pink) &rarr; Exchange (Orange) &rarr; Empty. The whole cell tints to match and the small square in the upper-right corner shows the current colour.</p>
                  <p><span className="font-semibold text-[#2D2560]">What this becomes:</span> The schedule you build here is rendered as a formatted 4-week table in your final Parenting Plan PDF &mdash; with cells tinted to match assignments and binding clauses describing the rotation.</p>
                </div>

                {/* Legend */}
                <div className="flex gap-3 mb-4 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10">
                    <span className="w-3 h-3 rounded-[3px] border border-white/30" style={{ background: '#3B82F6' }}></span>
                    <span className="text-xs font-medium text-[#3B82F6]">Plaintiff Overnight</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10">
                    <span className="w-3 h-3 rounded-[3px] border border-white/30" style={{ background: '#EC4899' }}></span>
                    <span className="text-xs font-medium text-[#EC4899]">Defendant Overnight</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10">
                    <span className="w-3 h-3 rounded-[3px] border border-white/30" style={{ background: '#F97316' }}></span>
                    <span className="text-xs font-medium text-[#F97316]">Exchanges</span>
                  </div>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-8 gap-1 mb-1">
                  <div className="text-xs text-[#3A3566] py-1 text-center"></div>
                  {WEEKDAYS.map(function(dn) {
                    return <div key={dn} className="text-center text-xs text-[#3A3566] py-1 font-semibold">{dn}</div>;
                  })}
                </div>

                {/* Week rows */}
                {[0, 1, 2, 3].map(function(weekIdx) {
                  return (
                    <div key={weekIdx} className="grid grid-cols-8 gap-1 mb-1" data-testid={'plan-week-' + (weekIdx + 1)}>
                      <div className="flex items-center justify-center text-xs text-[#2D2560] font-bold">Wk {weekIdx + 1}</div>
                      {WEEKDAYS.map(function(dn, dayIdx) {
                        var overnightVal = (planData.weekGridOvernight && planData.weekGridOvernight[weekIdx]) ? planData.weekGridOvernight[weekIdx][dayIdx] : '';
                        var overnightColor = overnightVal === 'plaintiff' ? '#3B82F6' : overnightVal === 'defendant' ? '#EC4899' : overnightVal === 'exchange' ? '#F97316' : null;
                        // Cell gets a subtle tint matching the assignment
                        var cellBg = overnightVal === 'plaintiff'
                          ? 'rgba(59,130,246,0.12)'
                          : overnightVal === 'defendant'
                          ? 'rgba(236,72,153,0.12)'
                          : overnightVal === 'exchange'
                          ? 'rgba(249,115,22,0.12)'
                          : 'rgba(255,255,255,0.03)';
                        var cellBorder = overnightVal
                          ? 'rgba(255,255,255,0.22)'
                          : 'rgba(255,255,255,0.08)';
                        var tooltipLabel = overnightVal === 'exchange'
                          ? 'Exchange Day'
                          : overnightVal
                          ? 'Overnight: ' + overnightVal.charAt(0).toUpperCase() + overnightVal.slice(1)
                          : 'Tap to cycle: Plaintiff / Defendant / Exchange';
                        return (
                          <button
                            type="button"
                            key={weekIdx + '-' + dayIdx}
                            data-testid={'plan-cell-' + weekIdx + '-' + dayIdx}
                            onClick={function(e) { rotateOvernight(weekIdx, dayIdx, e); }}
                            className="aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-semibold relative cursor-pointer transition-all hover:shadow-[0_0_0_2px_rgba(90,78,158,0.45)]"
                            style={{
                              backgroundColor: cellBg,
                              border: '2px solid rgba(90,78,158,0.55)',
                              color: 'rgba(30,26,62,0.85)',
                            }}
                            title={tooltipLabel}
                            aria-label={'Set overnight for week ' + (weekIdx + 1) + ' ' + dn}
                          >
                            {/* Upper-right indicator — shows the current assignment colour. Decorative; the whole cell is the click target. */}
                            <span
                              data-testid={'plan-overnight-' + weekIdx + '-' + dayIdx}
                              aria-hidden="true"
                              className="absolute top-1 right-1 w-3.5 h-3.5 rounded-[3px] border pointer-events-none"
                              style={overnightColor
                                ? { background: overnightColor, borderColor: '#FFFFFF' }
                                : { background: 'rgba(45,37,96,0.18)', borderColor: 'rgba(90,78,158,0.4)' }}
                            />
                            <span>{dn.charAt(0)}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Counts — an "Exchange" day counts toward total Exchanges AND toward the
                    overnight total of whichever parent is assigned the day immediately after. */}
                <div className="flex gap-4 mt-3 text-xs text-[#3A3566] flex-wrap font-medium">
                  {[
                    { key: 'plaintiff', label: 'Plaintiff Overnights', color: '#3B82F6' },
                    { key: 'defendant', label: 'Defendant Overnights', color: '#EC4899' },
                    { key: 'exchange', label: 'Exchanges', color: '#F97316' },
                  ].map(function(row) {
                    var grid = planData.weekGridOvernight || [];
                    // Flatten to 28-day sequence (wrap Wk4 Sat -> Wk1 Sun for attribution)
                    var flat = [];
                    for (var w = 0; w < grid.length; w++) {
                      for (var d = 0; d < (grid[w] || []).length; d++) {
                        flat.push(grid[w][d]);
                      }
                    }
                    var count = 0;
                    flat.forEach(function(c, i) {
                      if (c === row.key) {
                        count++;
                      } else if (c === 'exchange' && (row.key === 'plaintiff' || row.key === 'defendant')) {
                        // Attribute the exchange overnight to the parent who holds the next day
                        var nextDay = flat[(i + 1) % flat.length];
                        if (nextDay === row.key) count++;
                      }
                    });
                    return (
                      <span key={'on-' + row.key} className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-[3px] border border-white/30" style={{ background: row.color }}></span>
                        {row.label}: {count}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Scheduled Events (supervised visitation sessions, therapy, other) */}
            <div className="card-surface p-5 space-y-3" data-testid="schedule-events-section">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Scheduled Events</p>
                  <p className="text-xs text-white/40 mt-1">Add specific dated events — e.g., supervised visitation sessions, therapy appointments, or other scheduled commitments involving the minor child.</p>
                </div>
                <button
                  onClick={addScheduleEvent}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white/50 hover:text-white/70 hover:bg-white/[0.06] transition-colors shrink-0"
                  data-testid="add-schedule-event-btn">
                  <Plus className="w-3 h-3" /> Add Event
                </button>
              </div>
              {scheduleEvents.length === 0 && (
                <p className="text-xs text-white/25 py-2">No events scheduled. Click "Add Event" to add one.</p>
              )}
              {scheduleEvents.map(function(ev, idx) {
                return (
                  <div key={ev.id || idx} className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-3" data-testid={'schedule-event-' + idx}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                        <div>
                          <label className="block text-xs text-white/40 mb-1">Event Type</label>
                          <select
                            value={ev.type || 'supervised-visitation'}
                            onChange={function(e) { updateScheduleEvent(idx, 'type', e.target.value); }}
                            className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                            data-testid={'schedule-event-type-' + idx}
                          >
                            <option value="supervised-visitation" className="bg-[#1C1F2E]">Supervised Visitation</option>
                            <option value="therapy" className="bg-[#1C1F2E]">Therapy / Counseling</option>
                            <option value="medical" className="bg-[#1C1F2E]">Medical Appointment</option>
                            <option value="school" className="bg-[#1C1F2E]">School Event</option>
                            <option value="extracurricular" className="bg-[#1C1F2E]">Extracurricular Activity</option>
                            <option value="exchange" className="bg-[#1C1F2E]">Special Exchange</option>
                            <option value="other" className="bg-[#1C1F2E]">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-white/40 mb-1">Event Name / Description</label>
                          <input
                            type="text"
                            value={ev.name || ''}
                            onChange={function(e) { updateScheduleEvent(idx, 'name', e.target.value); }}
                            className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                            placeholder="e.g., Weekly supervised visitation"
                            data-testid={'schedule-event-name-' + idx}
                          />
                        </div>
                      </div>
                      <button
                        onClick={function() { removeScheduleEvent(idx); }}
                        className="p-2 text-white/20 hover:text-[#EF4444] transition-colors shrink-0 mt-5"
                        data-testid={'remove-schedule-event-' + idx}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-white/40 mb-1">Day of Week</label>
                        <select
                          value={ev.dayOfWeek || ''}
                          onChange={function(e) { updateScheduleEvent(idx, 'dayOfWeek', e.target.value); }}
                          className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid={'schedule-event-day-' + idx}
                        >
                          <option value="" className="bg-[#1C1F2E]">Select day</option>
                          <option value="Sunday" className="bg-[#1C1F2E]">Sunday</option>
                          <option value="Monday" className="bg-[#1C1F2E]">Monday</option>
                          <option value="Tuesday" className="bg-[#1C1F2E]">Tuesday</option>
                          <option value="Wednesday" className="bg-[#1C1F2E]">Wednesday</option>
                          <option value="Thursday" className="bg-[#1C1F2E]">Thursday</option>
                          <option value="Friday" className="bg-[#1C1F2E]">Friday</option>
                          <option value="Saturday" className="bg-[#1C1F2E]">Saturday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-white/40 mb-1">Frequency</label>
                        <select
                          value={ev.frequency || 'weekly'}
                          onChange={function(e) { updateScheduleEvent(idx, 'frequency', e.target.value); }}
                          className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid={'schedule-event-frequency-' + idx}
                        >
                          <option value="weekly" className="bg-[#1C1F2E]">Weekly</option>
                          <option value="biweekly" className="bg-[#1C1F2E]">Every Other Week</option>
                          <option value="monthly" className="bg-[#1C1F2E]">Monthly</option>
                          <option value="first-of-month" className="bg-[#1C1F2E]">First of the Month</option>
                          <option value="last-of-month" className="bg-[#1C1F2E]">Last of the Month</option>
                          <option value="as-needed" className="bg-[#1C1F2E]">As Needed</option>
                          <option value="one-time" className="bg-[#1C1F2E]">One-Time</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-white/40 mb-1">Time</label>
                        <input
                          type="time"
                          value={ev.time || ''}
                          onChange={function(e) { updateScheduleEvent(idx, 'time', e.target.value); }}
                          className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid={'schedule-event-time-' + idx}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/40 mb-1">Duration</label>
                        <input
                          type="text"
                          value={ev.duration || ''}
                          onChange={function(e) { updateScheduleEvent(idx, 'duration', e.target.value); }}
                          className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                          placeholder="e.g., 2 hours"
                          data-testid={'schedule-event-duration-' + idx}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">Location / Facility</label>
                      <input
                        type="text"
                        value={ev.location || ''}
                        onChange={function(e) { updateScheduleEvent(idx, 'location', e.target.value); }}
                        className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., Kids' First Family Visitation Center, 123 Main St, Anytown, ST"
                        data-testid={'schedule-event-location-' + idx}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">Notes (optional)</label>
                      <input
                        type="text"
                        value={ev.notes || ''}
                        onChange={function(e) { updateScheduleEvent(idx, 'notes', e.target.value); }}
                        className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                        placeholder="Additional context or conditions"
                        data-testid={'schedule-event-notes-' + idx}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Exchange and School settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Exchange Location</label>
                <input type="text" value={planData.exchangeLocation}
                  onChange={function(e) { updateData('exchangeLocation', e.target.value); }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
                  placeholder="e.g., School, Public location" data-testid="exchange-location" />
              </div>
            </div>

            {/* School session toggle */}
            <div className="card-surface p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Is the child currently in school?</label>
                <div className="flex gap-4">
                  {['yes', 'no'].map(function(opt) {
                    return (
                      <button key={opt} onClick={function() { updateData('childInSchool', opt); }}
                        data-testid={'school-' + opt}
                        className={planData.childInSchool === opt
                          ? 'flex-1 p-3 rounded-lg border transition-all border-[#5A4E9E] bg-[#5A4E9E]/10'
                          : 'flex-1 p-3 rounded-lg border transition-all border-white/10 hover:border-white/20'}>
                        {opt === 'yes' ? 'Yes' : 'No / Not Yet'}
                      </button>
                    );
                  })}
                </div>
              </div>
              {planData.childInSchool === 'yes' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Exchange Time When School is Out of Session</label>
                  <select value={planData.schoolOutExchangeTime}
                    onChange={function(e) { updateData('schoolOutExchangeTime', e.target.value); }}
                    className="w-full bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] border border-[#5A4E9E] text-white rounded-lg px-4 py-2"
                    data-testid="school-out-exchange-time">
                    <option value="" className="bg-[#2D2560] text-white">Select time</option>
                    {EXCHANGE_TIME_PRESETS.map(function(t) { return <option key={t} value={t} className="bg-[#2D2560] text-white">{t}</option>; })}
                  </select>
                  <p className="text-xs text-white/40 mt-1">Exchange time used during summer break, holidays, and non-school days</p>
                </div>
              )}
              {planData.childInSchool === 'yes' && (
                <div>
                  <label className="block text-sm font-medium mb-2">School-Day Custody Transition</label>
                  <p className="text-xs text-white/40 mb-3">When school is in session, does the receiving parent's custody begin at morning drop-off or at end-of-day pickup?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { id: 'morning-dropoff', label: 'Morning Drop-Off', desc: 'Receiving parent drops child at school in the morning — custody starts at drop-off' },
                      { id: 'end-of-day-pickup', label: 'End-of-Day Pickup', desc: 'Receiving parent picks child up after school — custody starts at pickup' },
                    ].map(function(opt) {
                      return (
                        <button key={opt.id} onClick={function() { updateData('schoolCustodyTransition', opt.id); }}
                          className={'p-4 rounded-lg border text-left transition-all ' +
                            (planData.schoolCustodyTransition === opt.id ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-white/10 hover:border-white/20')}
                          data-testid={'custody-transition-' + opt.id}>
                          <p className="text-sm font-medium">{opt.label}</p>
                          <p className="text-xs text-white/40 mt-1">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* Supervised Visitation Section */}
            {planData.supervisedVisitation === 'yes' && (
              <div className="card-surface p-5 space-y-4 border-l-2 border-[#EF4444]/40" data-testid="supervised-visitation-section">
                <div>
                  <p className="text-sm font-semibold text-[#EF4444] mb-1">Supervised Visitation Schedule</p>
                  <p className="text-xs text-white/40">Define the visitation schedule for the non-custodial parent under supervision.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Visitation Frequency</label>
                  <div className="space-y-2">
                    {[
                      { id: 'weekly', name: 'Weekly', desc: 'Once per week' },
                      { id: 'biweekly', name: 'Every Other Week', desc: 'Once every two weeks' },
                      { id: 'monthly', name: 'Monthly', desc: 'Once per month' },
                      { id: 'custom-sv', name: 'Custom', desc: 'Specify a custom schedule' },
                    ].map(function(opt) {
                      return (
                        <button key={opt.id} onClick={function() { updateData('supervisedVisitationSchedule', opt.id); }}
                          data-testid={'sv-freq-' + opt.id}
                          className={'w-full p-3 rounded-lg border text-left transition-all ' +
                            (planData.supervisedVisitationSchedule === opt.id ? 'border-[#EF4444] bg-[#EF4444]/10' : 'border-white/10 hover:border-white/20')}>
                          <p className="text-sm font-medium">{opt.name}</p>
                          <p className="text-xs text-white/40">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Visitation Day</label>
                    <select value={planData.supervisedVisitationDay}
                      onChange={function(e) { updateData('supervisedVisitationDay', e.target.value); }}
                      className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-4 py-2"
                      data-testid="sv-day">
                      <option value="" className="bg-[#1C1F2E] text-white">Select day</option>
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(function(d) {
                        return <option key={d} value={d} className="bg-[#1C1F2E] text-white">{d}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Start Time</label>
                    <select value={planData.supervisedVisitationTime}
                      onChange={function(e) { updateData('supervisedVisitationTime', e.target.value); }}
                      className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-4 py-2"
                      data-testid="sv-time">
                      <option value="" className="bg-[#1C1F2E] text-white">Select time</option>
                      {EXCHANGE_TIME_PRESETS.map(function(t) { return <option key={t} value={t} className="bg-[#1C1F2E] text-white">{t}</option>; })}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration</label>
                    <select value={planData.supervisedVisitationDuration}
                      onChange={function(e) { updateData('supervisedVisitationDuration', e.target.value); }}
                      className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-4 py-2"
                      data-testid="sv-duration">
                      <option value="" className="bg-[#1C1F2E] text-white">Select duration</option>
                      <option value="1 hour" className="bg-[#1C1F2E] text-white">1 hour</option>
                      <option value="1.5 hours" className="bg-[#1C1F2E] text-white">1.5 hours</option>
                      <option value="2 hours" className="bg-[#1C1F2E] text-white">2 hours</option>
                      <option value="3 hours" className="bg-[#1C1F2E] text-white">3 hours</option>
                      <option value="4 hours" className="bg-[#1C1F2E] text-white">4 hours</option>
                      <option value="6 hours" className="bg-[#1C1F2E] text-white">6 hours</option>
                      <option value="8 hours" className="bg-[#1C1F2E] text-white">8 hours</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Facility / Provider Name</label>
                    <input type="text" value={planData.supervisedVisitationFacilityName || ''}
                      onChange={function(e) { updateData('supervisedVisitationFacilityName', e.target.value); }}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
                      placeholder="e.g., Kids' First Family Visitation Center"
                      data-testid="sv-facility-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Visitation Location / Address</label>
                    <input type="text" value={planData.supervisedVisitationLocation}
                      onChange={function(e) { updateData('supervisedVisitationLocation', e.target.value); }}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2"
                      placeholder="e.g., 123 Main Street, Anytown, ST 12345"
                      data-testid="sv-location" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Supervisor</label>
                  <select value={planData.supervisedVisitationSupervisor}
                    onChange={function(e) { updateData('supervisedVisitationSupervisor', e.target.value); }}
                    className="w-full bg-[#1C1F2E] border border-white/10 text-white rounded-lg px-4 py-2"
                    data-testid="sv-supervisor">
                    <option value="" className="bg-[#1C1F2E] text-white">Select supervisor type</option>
                    <option value="professional" className="bg-[#1C1F2E] text-white">Professional supervisor / agency</option>
                    <option value="family-member" className="bg-[#1C1F2E] text-white">Approved family member</option>
                    <option value="mutual-third-party" className="bg-[#1C1F2E] text-white">Mutually agreed third party</option>
                    <option value="court-appointed" className="bg-[#1C1F2E] text-white">Court-appointed supervisor</option>
                  </select>
                </div>
              </div>
            )}

          </div>
        
  );
};
